import { PropertyType as PropertyTypeEnum, RoomIcon } from '@/entities/room'
import type { RoomIconName } from '@/entities/room'
import { useMultiScrollState } from '@/shared/lib'

import { FilterChip } from './filter-chip'
import { ExpandButton, useExpand } from '../../lib/expand'
import { Filter, useFilter } from '../../model/filter'

type PropertyType = {
  title: string
  value: PropertyTypeEnum
  icon: RoomIconName
}

const PROPERTY_TYPES: PropertyType[] = [
  {
    title: '오피스텔',
    value: PropertyTypeEnum.OFFICETEL,
    icon: 'officetel',
  },
  {
    title: '아파트',
    value: PropertyTypeEnum.APARTMENT,
    icon: 'apartment',
  },
  {
    title: '연립빌라',
    value: PropertyTypeEnum.VILLA,
    icon: 'villa',
  },
  {
    title: '단독주택',
    value: PropertyTypeEnum.DETACHED,
    icon: 'detached',
  },
  {
    title: '원룸건물',
    value: PropertyTypeEnum.STUDIO,
    icon: 'studio',
  },
  {
    title: '상가주택',
    value: PropertyTypeEnum.MIXED_USE,
    icon: 'mixedUse',
  },
  {
    title: '고시원',
    value: PropertyTypeEnum.GOSIWON,
    icon: 'gosiwon',
  },
  {
    title: '호텔',
    value: PropertyTypeEnum.HOTEL,
    icon: 'hotel',
  },
  {
    title: '쉐어\n하우스',
    value: PropertyTypeEnum.SHARE_HOUSE,
    icon: 'shareHouse',
  },
  {
    title: '펜션',
    value: PropertyTypeEnum.VACATION_HOME,
    icon: 'vacationHome',
  },
  {
    title: '게스트\n하우스',
    value: PropertyTypeEnum.GUEST_HOUSE,
    icon: 'guestHouse',
  },
  {
    title: '모텔여관',
    value: PropertyTypeEnum.MOTEL,
    icon: 'motel',
  },
]

export const PropertyTypes = () => {
  const { target, registerScrollElement, scrollIntoView } =
    useMultiScrollState<HTMLDivElement>()

  const { expanded, toggleExpand } = useExpand({
    defaultExpanded: target === Filter.PropertyTypes,
  })

  const {
    filter: { propertyTypes },
    updateFilter,
  } = useFilter()

  const propertyTypeList = expanded
    ? PROPERTY_TYPES
    : PROPERTY_TYPES.slice(0, 4)

  const handleExpandButtonClick = () => {
    toggleExpand()

    requestAnimationFrame(() => {
      scrollIntoView(Filter.PropertyTypes, {
        behavior: 'smooth',
        block: 'start',
      })
    })
  }

  const handlePropertyTypeChange = (value: PropertyTypeEnum) => () => {
    updateFilter({ propertyTypes: value })
  }

  return (
    <div
      className="p-6 pt-0 pr-4"
      ref={element => {
        if (element) {
          registerScrollElement(Filter.PropertyTypes, element)
        }
      }}
    >
      <h3 className="typo-body1 mb-3 font-semibold text-black">건물 유형</h3>
      <ul className="flex flex-wrap gap-2">
        {propertyTypeList.map(({ title, value, icon }) => (
          <li key={title}>
            <FilterChip
              size="large"
              selected={propertyTypes.includes(value)}
              onClick={handlePropertyTypeChange(value)}
            >
              <RoomIcon name={icon} width={50} height={38} className="mb-1" />
              <p className="flex h-11 items-center justify-center whitespace-break-spaces">
                {title}
              </p>
            </FilterChip>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex justify-center">
        <ExpandButton expanded={expanded} onClick={handleExpandButtonClick} />
      </div>
    </div>
  )
}
